import React from 'react';
import DataGrid from '#/common/components/DataGrid';
import If from '#/common/components/If';
import { gql } from '@apollo/client';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { getFsmWorkOrderList } from '#/graphql/queries';
import type { Filter } from '#/common/components/DataGrid';
import { actions } from './actions';
import { fsmFlatColumns, fsmLineItemsVisibleColumns } from './fsmColumns';
import WorkOrderFsmList from '../WorkOrderFsmList/WorkOrderFsmList';

const getFsmWorkOrderListQuery = gql(getFsmWorkOrderList);

interface WorkOrderDataGridFsmFlatProps {
  omitColumns?: string[];
  permanentFilters?: Filter[];
  toolbarPrefix?: React.ReactNode;
  visibleColumns?: string[];
  gridId?: string;
}

/**
 * Flat FSM WOLI grid — replaces the grouped FSM parent grid for the FSM tab.
 *
 * Renders child work orders (line items) in a plain flat table.
 * Parent WO Number and Parent Title columns are visible by default so users
 * can identify which parent each line item belongs to.
 *
 * The grouped implementation (WorkOrderDataGridFsm) is kept for reference
 * but is no longer used on the FSM tab.
 */
const WorkOrderDataGridFsmFlat: React.FC<WorkOrderDataGridFsmFlatProps> = ({
  omitColumns,
  permanentFilters,
  toolbarPrefix,
  visibleColumns = fsmLineItemsVisibleColumns,
  gridId = 'work-orders-fsm-flat',
}) => {
  const { isDesktop } = useViewport();

  const cols = React.useMemo(() => {
    let filtered = fsmFlatColumns.filter((c) => !(omitColumns ?? []).includes(c.dataField));

    if (visibleColumns.length > 0) {
      filtered = filtered.map((col) => ({
        ...col,
        visible: visibleColumns.includes(col.dataField),
      }));
    }

    return filtered;
  }, [omitColumns, visibleColumns]);

  return (
    <>
      <If condition={isDesktop}>
        <DataGrid
          id={gridId}
          pluralText="Work Orders"
          singularText="Work Order"
          columns={cols}
          getItemsQuery={getFsmWorkOrderListQuery}
          recordUrl="/work-orders/:id"
          actions={actions}
          selectionMode="none"
          stateStoringColumnsOnly
          permanentFilters={permanentFilters}
          toolbarPrefix={toolbarPrefix}
        />
      </If>
      <If condition={!isDesktop}>
        <WorkOrderFsmList permanentFilters={permanentFilters} />
      </If>
    </>
  );
};

export default React.memo(WorkOrderDataGridFsmFlat);
